/**
 * `A new version is ready — tap to refresh.`
 *
 * The one thing on screen that says the bundle in this phone is no longer the bundle on the
 * server — the Technical Delegate's live-testing finding of 2026-09-09, which is written up in
 * full at the top of `version.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A PASSIVE BAR AND NOT A DIALOGUE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A UM meets this while doing something else: half a sentence into a note at the side of a
 * pitch, or reading a debrief pack with a panel waiting. A modal would take the keyboard away
 * from them, and an automatic reload would take the sentence. So the bar sits at the top of
 * the shell, takes no focus, moves nothing it does not need to, and does exactly nothing until
 * it is tapped. `location.reload()` is the whole of the action, and only a tap causes it.
 *
 * **Dismissal is per release, not for ever.** `Not Now` hides THIS release's bar; the watch
 * keeps running, so the deploy after it says so again. The dismissal lives in
 * `sessionStorage`, which means it survives a route change and dies with the tab — the same
 * scope, and the same wrapped access, as the resume destination in `resume.ts`.
 *
 * **The tone is the sync chip's, not an alert's.** Nothing is wrong: the app in hand works,
 * and a newer one exists. So this is `--accent-wash` and a hairline, at the size of a kicker,
 * and it never uses the alert red that means a UM has lost something.
 */

import { useCallback, useEffect, useState } from "react";

import { startVersionWatch } from "./version.js";

/** This tab's "not now", holding the release name it was said about. */
export const REFRESH_DISMISS_KEY = "hockeytd.refresh.dismissed";

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(REFRESH_DISMISS_KEY);
  } catch {
    // Storage refused (Safari private mode, blocked third-party storage). The bar simply
    // comes back on the next mount, which is a great deal better than a white page.
    return null;
  }
}

function writeDismissed(release: string): void {
  try {
    window.sessionStorage.setItem(REFRESH_DISMISS_KEY, release);
  } catch {
    // As above: losing a dismissal is a tap, an exception is a screen.
  }
}

export interface NewVersionState {
  /** The deployed release name when it differs from the running one and has not been waved away. */
  newRelease: string | null;
  /** Hide the bar for this release only. */
  dismiss: () => void;
}

/**
 * The watch, as React state.
 *
 * `runningRelease` is a parameter rather than a read of `__RELEASE__` inside this hook, for
 * the reason every seam in this package exists: under vitest the compiled-in value is `dev`,
 * and a hook that could only ever be handed `dev` could not be tested against the case that
 * matters. {@link RefreshBanner} passes the real one.
 */
export function useNewVersion(
  runningRelease: string,
  intervalMs?: number,
): NewVersionState {
  const [deployed, setDeployed] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(() => readDismissed());

  useEffect(() => {
    return startVersionWatch({
      runningRelease,
      onNewVersion: setDeployed,
      ...(intervalMs === undefined ? {} : { intervalMs }),
    });
  }, [runningRelease, intervalMs]);

  const dismiss = useCallback(() => {
    if (deployed === null) return;
    writeDismissed(deployed);
    setDismissed(deployed);
  }, [deployed]);

  return { newRelease: deployed === null || deployed === dismissed ? null : deployed, dismiss };
}

export interface RefreshBannerProps {
  /** Defaults to the release compiled into this bundle. Overridden only by the suite. */
  runningRelease?: string;
  /** Defaults to five minutes. Overridden only by the suite. */
  intervalMs?: number;
}

export function RefreshBanner(props: RefreshBannerProps = {}): React.JSX.Element | null {
  const { newRelease, dismiss } = useNewVersion(
    props.runningRelease ?? __RELEASE__,
    props.intervalMs,
  );

  if (newRelease === null) return null;

  return (
    <div className="verbar" data-testid="refresh-banner">
      {/*
       * A button, not a link and not a whole-bar click target: a bar that reloaded the page
       * wherever it was touched would fire on a mis-swipe while scrolling a note list.
       */}
      <button
        type="button"
        className="verbar__act"
        data-testid="refresh-banner-reload"
        onClick={() => {
          window.location.reload();
        }}
      >
        A new version is ready — tap to refresh.
      </button>
      <button
        type="button"
        className="verbar__dismiss"
        data-testid="refresh-banner-dismiss"
        aria-label="Hide this until the next version"
        onClick={dismiss}
      >
        Not Now
      </button>
    </div>
  );
}
